import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Clock, Calendar, MapPin } from 'lucide-react';
import { GroupInfo } from '@/hooks/useUserGroupInfo';

interface GroupScheduleMatrixProps {
  groupInfo: GroupInfo;
}

const GroupScheduleMatrix = ({ groupInfo }: GroupScheduleMatrixProps) => {
  const { groupName, category, subcategory, date, startTime, endTime, court, participants, matches } = groupInfo;

  // Build the matrix
  // Rows: each participant, Columns: opponent number (1, 2, 3...)
  const buildMatrix = () => {
    const matrix: (string | null)[][] = [];
    
    participants.forEach((participant, rowIdx) => {
      const row: (string | null)[] = [];
      
      participants.forEach((opponent, colIdx) => {
        if (rowIdx === colIdx) {
          // Diagonal - same team
          row.push(null);
        } else {
          // Find the match between these two participants
          const match = matches.find(m => {
            const [p1, p2] = m.participants;
            return (
              (p1 === participant.letter && p2 === opponent.letter) ||
              (p2 === participant.letter && p1 === opponent.letter)
            );
          });
          
          if (match) {
            // Use match.date directly (e.g., "Jueves") if available
            const dayName = match.date || '';
            const timeStr = match.time;
            row.push(dayName ? `${dayName}\n${timeStr}` : timeStr);
          } else {
            row.push('Por programar');
          }
        }
      });
      
      matrix.push(row);
    });
    
    return matrix;
  };

  const matrix = buildMatrix();
  const totalMatches = matches.length;
  const totalParticipants = participants.length;

  return (
    <Card className="border-2 border-primary/20 dark:border-primary/30">
      <CardHeader className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-t-lg pb-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg md:text-xl font-bold">{groupName}</h3>
          </div>
          
          {category && (
            <div className="text-sm font-medium opacity-90">
              {category}{subcategory ? ` - ${subcategory}` : ''}
            </div>
          )}
          
          <div className="flex flex-wrap gap-2 md:gap-3 text-xs md:text-sm">
            {date && (
              <div className="flex items-center gap-1 md:gap-1.5 bg-white/10 px-2 py-1 md:px-3 md:py-1.5 rounded-full">
                <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                <span>{date}</span>
              </div>
            )}
            
            {startTime && endTime && (
              <div className="flex items-center gap-1 md:gap-1.5 bg-white/10 px-2 py-1 md:px-3 md:py-1.5 rounded-full">
                <Clock className="w-3 h-3 md:w-4 md:h-4" />
                <span>{startTime} - {endTime}</span>
              </div>
            )}
            
            {court && (
              <div className="flex items-center gap-1 md:gap-1.5 bg-white/10 px-2 py-1 md:px-3 md:py-1.5 rounded-full">
                <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                <span>Cancha {court}</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        {participants.length > 0 ? (
          <>
            {/* Matrix Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-primary/5 dark:bg-primary/10">
          <th className="border border-primary/30 dark:border-primary/20 px-1 py-1 md:px-3 md:py-2 text-left text-xs md:text-sm font-semibold text-primary dark:text-primary min-w-[90px] md:min-w-0">
            <span className="hidden md:inline">Pareja</span>
            <span className="md:hidden">Participantes</span>
          </th>
                    {participants.map((_, idx) => (
                      <th 
                        key={idx}
                        className="border border-primary/30 dark:border-primary/20 px-1 py-1 md:px-2 md:py-2 text-center text-xs md:text-sm font-semibold text-primary dark:text-primary"
                      >
                        {idx + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {participants.map((participant, rowIdx) => (
                    <tr 
                      key={participant.id}
                      className={participant.isUser ? 'bg-primary/5 dark:bg-primary/10' : ''}
                    >
                      <td className={`border border-primary/30 dark:border-primary/20 px-1 py-1 md:px-3 md:py-2 text-xs md:text-sm ${
                        participant.isUser ? 'font-bold text-primary dark:text-primary' : 'text-gray-700 dark:text-gray-300'
                      }`}>
          {/* Vista móvil: nombres completos */}
          <div className="flex md:hidden flex-col items-start gap-0.5 py-1">
            <span className="text-[10px] leading-tight font-medium truncate max-w-[100px]">
              {participant.player1}
            </span>
            {participant.player2 && (
              <span className="text-[9px] leading-tight opacity-75 truncate max-w-[100px]">
                {participant.player2}
              </span>
            )}
          </div>
                        
                        {/* Vista desktop: letra + nombres */}
                        <div className="hidden md:flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary text-xs font-bold flex-shrink-0">
                            {participant.letter}
                          </span>
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">{participant.player1}</span>
                            {participant.player2 && (
                              <span className="text-xs opacity-75 truncate">
                                {participant.player2}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {matrix[rowIdx].map((cell, colIdx) => (
                        <td 
                          key={colIdx}
                          className={`border border-primary/30 dark:border-primary/20 px-1 py-1 md:px-2 md:py-2 text-center text-[10px] leading-tight md:text-xs ${
                            cell === null 
                              ? 'bg-gray-200 dark:bg-gray-800 text-gray-500' 
                              : cell === 'Por programar'
                              ? 'text-gray-400 dark:text-gray-500 italic'
                              : 'text-primary dark:text-primary font-medium'
                          }`}
                        >
                          {cell === null ? '—' : (
                            <span className="whitespace-pre-line">{cell}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                <span className="font-semibold text-primary dark:text-primary">{totalParticipants} parejas</span>
                {' • '}
                <span className="font-semibold text-primary dark:text-primary">{totalMatches} partidos</span>
                {totalMatches === 0 && ' (pendientes de programación)'}
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-sm">No hay participantes asignados al grupo aún</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GroupScheduleMatrix;
